import { gql } from '@apollo/client';
import {
  AlertDescription,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import Select from 'react-select';
import { z } from 'zod';
import { WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { PlaylistDuplicateInput, PlaylistType } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { usePlaylistDuplicateMutation } from './__generated__/DuplicatePlaylistModal.graphql';

const contentTypeOption = selectOptionBaseSchema;

const FormData = z.object({
  title: z
    .string()
    .min(1)
    .regex(/.*\S.*/, t('titleSpaceWarn')),
  description: z.string().optional(),
  contentType: contentTypeOption.nullish(),
});
type FormData = z.infer<typeof FormData>;

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  playlistId: string;
  playlistType: PlaylistDuplicateInput['targetPlaylistContentType'];
  playlistName: string;
}

export function DuplicatePlaylistModal({
  isOpen,
  onCancel,
  playlistId,
  playlistType,
  playlistName,
}: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <DuplicatePlaylistModalContent
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          playlistId={playlistId}
          playlistType={playlistType}
          playlistName={playlistName}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onCancel: () => void;
  playlistId: PlaylistDuplicateInput['sourcePlaylistId'];
  playlistType: PlaylistDuplicateInput['targetPlaylistContentType'];
  playlistName: string;
};

interface ContentTypeOption {
  label: string;
  value: string;
}

function DuplicatePlaylistModalContent({
  initialFocusRef,
  onCancel,
  playlistId,
  playlistType,
  playlistName,
}: ContentProps) {
  const toast = useToast();
  const options = useMemo<ContentTypeOption[]>(
    () => [
      { label: t('imagesAndVideo'), value: PlaylistType.Mixed },
      { label: t('imageOnly'), value: PlaylistType.Image },
    ],
    [],
  );
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormData>({
    defaultValues: {
      title: playlistName,
      contentType: options.find((opt) => opt.value === playlistType),
    },
    resolver: zodResolver(FormData),
    mode: 'onChange',
  });

  // const selectedContentType = watch('contentType')?.value as PlaylistType | undefined;

  const { ref: titleInputRef, ...titleInputProps } = register('title');

  const [duplicatePlaylist] = usePlaylistDuplicateMutation();
  const [showMainHeader, setShowMainHeader] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showMixedModal, setShowMixedModal] = useState(false);
  // const showMainHeader = selectedContentType === playlistType || !selectedContentType
  // const showImageModal = selectedContentType === PlaylistType.Image && playlistType !== selectedContentType;
  // const showMixedModal = selectedContentType === PlaylistType.Mixed && playlistType !== selectedContentType;

  const handlePreSubmit = (source: 'create' | 'notCreate') => {
    const selected = watch('contentType')?.value as PlaylistType | undefined;

    const isSame = selected === playlistType || !selected;
    const isImage = selected === PlaylistType.Image && playlistType !== selected;
    const isMixed = selected === PlaylistType.Mixed && playlistType !== selected;

    setShowMainHeader(isSame);
    setShowImageModal(isImage);
    setShowMixedModal(isMixed);

    if (source === 'create' && isSame) {
      handleSubmit(onSubmit)();
    } else if (source === 'notCreate' && (isImage || isMixed)) {
      handleSubmit(onSubmit)();
    }
  };
  const onSubmit = useCallback(
    async ({ title, description, contentType }: FormData) => {
      try {
        const { data } = await duplicatePlaylist({
          variables: {
            input: {
              sourcePlaylistId: playlistId,
              targetPlaylistName: title,
              targetPlaylistDescription: description,
              targetPlaylistContentType: contentType?.value as PlaylistType,
            },
          },
        });

        if (!data || !data.playlistDuplicate.playlist) {
          throw new Error('Duplicate playlist failed');
        }
      } catch (err) {
        toast({
          status: 'error',
          title: t('duplicatePlaylistErr'),
          description: fromError(err, 'DuplicatePlaylist'),
        });
      } finally {
        onCancel();
      }
    },
    [duplicatePlaylist, playlistId, toast, onCancel],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {showMainHeader && <ModalHeader>{t('duplicatePlaylist')}</ModalHeader>}
      <ModalCloseButton onClick={onCancel} />
      {showMainHeader && (
        <ModalBody>
          <VStack>
            <WarningAlert>
              <AlertDescription>{t('defaultPlaylistDuration')}</AlertDescription>
            </WarningAlert>
            <FormControl isRequired isInvalid={Boolean(errors.title)}>
              <FormLabel>{t('playlistName')}</FormLabel>
              <Input
                ref={(r) => {
                  titleInputRef(r);
                  initialFocusRef.current = r;
                }}
                placeholder={t('playlistTitlePlaceholder')}
                {...titleInputProps}
              />
              <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.description)}>
              <FormLabel>{t('description')}</FormLabel>
              <Input
                type="textarea"
                placeholder={t('playlistDescPlaceholder')}
                {...register('description')}
              />
              <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.contentType)}>
              <FormLabel>{t('playlistContentType')}</FormLabel>
              <Controller
                name="contentType"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    options={options}
                    components={components}
                    onChange={field.onChange}
                    isLoading={isSubmitting}
                    isMulti={false}
                    escapeClearsValue={false}
                    isClearable={false}
                    isSearchable={false}
                    menuPlacement="auto"
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="contentType"
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FormControl>
          </VStack>
        </ModalBody>
      )}
      {showImageModal && (
        <ModalBody pb={3}>
          <VStack spacing={4}>
            <Box px={5} pt={5} borderRadius="md">
              <Box fontWeight="bold" fontSize="lg" mb={4}>
                {t('duplicatePlaylistImage')}
              </Box>
              <Box mt={2} pt={5} fontSize="sm">
                <Trans
                  i18nKey="changePlaylistTypeToImageOnly"
                  components={{ strong: <strong /> }}
                />
              </Box>
              <Box fontSize="sm">
                <Trans
                  i18nKey="changePlaylistTypeToImageOnlyDesc1"
                  components={{ strong: <strong /> }}
                />
              </Box>

              <Box mt={2} pt={2} fontSize="sm">
                <Trans
                  i18nKey="changePlaylistTypeToImageOnlyDesc2"
                  components={{ strong: <strong /> }}
                />
              </Box>
            </Box>
          </VStack>
        </ModalBody>
      )}
      {showMixedModal && (
        <ModalBody pb={2}>
          <VStack spacing={4}>
            <Box px={5} pt={5} borderRadius="md">
              <Box fontWeight="bold" fontSize="lg" mb={4}>
                {t('duplicatePlaylistMixed')}
              </Box>
              <Box mt={2} pt={5} fontSize="sm">
                <Trans i18nKey="changePlaylistTypeToMixed" components={{ strong: <strong /> }} />
              </Box>
              <Box mt={2} pt={2} fontSize="sm">
                {t('changePlaylistTypeToMixedDesc')}
              </Box>
            </Box>
          </VStack>
        </ModalBody>
      )}
      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
          {t('cancel')}
        </Button>
        {showMainHeader && (
          <Button
            variant="solid"
            colorScheme={'blue'}
            marginLeft="3"
            type="button"
            isDisabled={!isValid || isSubmitting || Object.keys(errors).length > 0}
            isLoading={isSubmitting}
            onClick={() => handlePreSubmit('create')}
          >
            {t('create')}
          </Button>
        )}
        {(showImageModal || showMixedModal) && (
          <Button
            variant="solid"
            colorScheme={'red'}
            marginLeft="3"
            type="button"
            isDisabled={!isValid || isSubmitting || Object.keys(errors).length > 0}
            isLoading={isSubmitting}
            onClick={() => handlePreSubmit('notCreate')}
          >
            {showImageModal
              ? t('changeToImageOnly')
              : showMixedModal
              ? t('changeToImageAndVideo')
              : undefined}
          </Button>
        )}
      </ModalFooter>
    </form>
  );
}

DuplicatePlaylistModal.graphql = {
  mutations: {
    PlaylistDuplicate: gql`
      mutation PlaylistDuplicate($input: PlaylistDuplicateInput!) {
        playlistDuplicate(input: $input) {
          playlist {
            id
            ...PlaylistTable_playlist
          }
        }
      }
    `,
  },
};
